import React, { FC, useCallback } from 'react';

import { Article } from '../../../models/Article';
import { ProductMetaData } from '../../../models/Product';
import { useOrderLists } from './useOrderLists';
import { NewSelectionListDropdown } from './NewSelectionListDropdown';
import { ListManager } from '../components/ListManager';
import { ButtonSize, ButtonTheme } from './Button';

import './SelectionListProductItemDropDown.scss';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';

type Props = {
    readonly product: ProductMetaData & Article;
    readonly isDebounced?: boolean;
    readonly showSelectionLists?: boolean;
    readonly onAddPrice?: (options: SingleAddOptions) => Promise<void>;
    readonly size?: ButtonSize;
    readonly theme?: ButtonTheme;
};

const SelectionListProductItemDropDown: FC<Props> = ({
    product,
    showSelectionLists = true,
    onAddPrice,
    size,
    theme
}) => {
    const { orderlists, activeList, createNewList } = useOrderLists();
    const activeListId = activeList?.id;

    // Can be commented out in order to visually debug what happens when orderlists are empty
    // (orderlists as any) = [];

    const handleActiveListClick = useCallback(async () => {
        if (activeListId && onAddPrice) {
            onAddPrice({ options: product, listId: activeListId, target: ListActionTarget.List });
        }
    }, [activeListId, onAddPrice, product]);

    const handleListItemClick = useCallback(
        (listId: string) => {
            if (onAddPrice) {
                onAddPrice({ options: product, listId: listId, target: ListActionTarget.List });
            } else {
                throw new Error('illegal list action');
            }
        },
        [onAddPrice, product]
    );

    if (!showSelectionLists) {
        return null;
    }

    if (orderlists.length < 1) {
        return <NewSelectionListDropdown onCreateSelectionList={createNewList} />;
    }

    return (
        <ListManager
            size={size}
            theme={theme}
            label={activeList?.listName}
            options={orderlists}
            onClick={handleActiveListClick}
            onSelect={handleListItemClick}
            onCreate={createNewList}
            disabled={false}
        />
    );
};

export default SelectionListProductItemDropDown;
